import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { FriendTopic } from '../../../../../../shared/friends-copy.js';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiFetch, type MeResponse } from '../../../auth';
import { apiErrorCode, apiFailureReason, emberApi, isAuthRequired, wordsRefused, type FriendCard, type FriendPronoun, type FriendWant } from '../../api';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { PresenceAvatar } from '../../components/PresenceAvatar';
import { useFriendsConfig } from '../../client-config';
import { goFriends, navigate } from '../../router';
import { useKeptAcrossReload } from '../../use-kept-across-reload';
import { BACK_CHEVRON, CARD, FriendsBar, NotAvailable, TILTS, TINTS, firstName, fitHeight, randomAvatar, zonePlace } from './friend-parts';

const STEPS = ['topics', 'card'] as const;
type Step = 0 | 1;
type Mode = 'new' | 'edit' | 'again';

const I_AM: ReadonlyArray<readonly [FriendPronoun, string]> = [['she', 'woman'], ['he', 'man'], ['they', 'non-binary']];
const TO_MEET: ReadonlyArray<readonly [FriendWant, string]> = [['anyone', 'anyone'], ['women', 'women'], ['men', 'men']];

const sameWord = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();
/** The chips: `first` (Ember's draft, their card, what they typed) ahead of the ones already there, each once. */
const withChips = (chips: string[], first: string[]): string[] => {
  const out: string[] = [];
  for (const c of [...first, ...chips]) if (c.trim() && !out.some((o) => sameWord(o, c))) out.push(c.trim());
  return out;
};
/** A chip's ground: its own on offer, else the next of the tints. The topics on offer (mock SetupTopics) come from the
 *  config (2026-11-01 Riley, src/shared/friends-copy.js) — Ember's draft and their own come first. */
const tintOf = (offered: readonly FriendTopic[], chip: string, i: number): string =>
  offered.find((t) => sameWord(t.label, chip))?.tint ?? TINTS[i % TINTS.length];

const CHECK = <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const PLUS = <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
const INFO = <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" fill="none" /><path d="M12 11v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="12" cy="7.8" r="1.1" fill="currentColor" /></svg>;
const SHUFFLE = <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path d="M4 7h11l-3-3M20 17H9l3 3" stroke="currentColor" strokeWidth="1.9" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const PENCIL = <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden><path d="M4 20h4L19 9l-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinejoin="round" /></svg>;
/**
 * Asking for a new friend — `/friends/new` (2026-10-26 founder; mocks SetupTopics, SetupIntro): two steps on one page
 * — what they could chat about for hours, and the card they show up as (name, picture, the line Ember drafted from their
 * conversation, their clock) — then "Find me a friend" (the this-or-that between them is gone since 2026-10-31 founder,
 * and the topic on offer "Dogs & cats" is "Pets": they became what two people were handed to open with). The first step opens
 * with who they are and who they'd like to meet (2026-10-28 founder): "I'm a…" set to their About-you pronouns when
 * there are some, and an answer that differs is written back there; "I'd like to meet…" anyone, or only women or men.
 * A request still looking opens here to change its card (`?edit=1`, or any arrival while one is looking);
 * `?from=<request_id>` starts again from a request they stopped; any other new request starts from the card of their
 * latest one (`last_card`), Ember's draft filling only what that card lacks. An introduction out sends them to it.
 */
export function FriendSetupPage({ search }: { search: URLSearchParams }): ReactElement {
  const fromId = search.get('from');
  const [state, setState] = useState<'loading' | 'ready' | 'unavailable' | 'failed'>('loading');
  const [mode, setMode] = useState<Mode>('new');
  const [step, setStep] = useState<Step>(0);
  // Their gender, set to their About-you pronouns (the one source; what they pick here is written back there).
  const [gender, setGender] = useState<FriendPronoun | null>(null);
  const [want, setWant] = useState<FriendWant>('anyone');
  // Meet people's served topics, limits and sentences (2026-11-01 Riley): the config's, redrawn when it changes
  const served = useFriendsConfig();
  const L = served.limits;
  const offered = served.topics.map((t) => t.label);
  const [chips, setChips] = useState<string[]>(() => offered);
  // The offer changed while the page was open: the chips keep what came from the person (their card, Ember's draft,
  // what they typed) and take the new offer behind them.
  const offeredBefore = useRef(offered);
  const offeredKey = offered.join('\n');
  useEffect(() => {
    const before = offeredBefore.current;
    offeredBefore.current = offered;
    if (before.join('\n') === offeredKey) return;
    setChips((c) => withChips(offered, c.filter((x) => !before.some((b) => sameWord(b, x)))));
  }, [offeredKey]);
  const [topics, setTopics] = useState<string[]>([]);
  const [other, setOther] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [line, setLine] = useState('');
  const [lineOpen, setLineOpen] = useState(false);
  const [draft, setDraft] = useState<'none' | 'waiting' | 'drafted'>('none');
  const [avatar, setAvatar] = useState(randomAvatar);
  // Their clock as the other side will see it (the server's one source, 2026-10-26), not this browser's.
  const [myZone, setMyZone] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // What the person already changed by hand is never overwritten by a draft that lands late.
  const touched = useRef({ topics: false, line: false });

  useEffect(() => {
    let alive = true;
    Promise.all([emberApi.friends(), apiFetch<MeResponse>('/api/me')])
      .then(([page, me]) => {
        if (!alive) return;
        if (!page.eligible) { setState('unavailable'); return; }
        const friend = page.friend ?? null;
        if (friend?.status === 'found') {
          const roomId = friend.intro?.room_id ?? friend.room_id;
          if (roomId) navigate(`/friends/intro/${encodeURIComponent(roomId)}`, { replace: true });
          else goFriends();
          return;
        }
        const looking = friend?.status === 'looking' ? friend.card : null;
        const stopped = !looking && fromId ? page.earlier?.stopped.find((s) => s.request_id === fromId)?.card ?? null : null;
        // A new request starts from the card they approved last time (2026-10-26 founder): they change only what they want.
        const last = !looking && !stopped ? page.last_card ?? null : null;
        const base: FriendCard | null = looking ?? stopped ?? last;
        setGender(me.profile?.pronouns ?? null);
        setMyZone(page.me?.zone ?? null);
        // Their own picture to start with, as the apps do; Shuffle changes it.
        if (page.me?.avatar) setAvatar(page.me.avatar);
        if (base) {
          setMode(looking ? 'edit' : stopped ? 'again' : 'new');
          if (base.want === 'women' || base.want === 'men') setWant(base.want);
          setChips((c) => withChips(c, base.topics || []));
          setTopics((base.topics || []).slice(0, L.topics_max));
          setName(base.name || firstName(me.self_name));
          setLine(base.line || '');
          if (base.avatar) setAvatar(base.avatar);
          touched.current = { topics: (base.topics || []).length > 0, line: Boolean(base.line) };
        } else {
          setName(firstName(me.self_name));
        }
        // Ember's draft fills only what the card they start from does not have (all of it, for a first request).
        if (!looking && !stopped && !(touched.current.topics && touched.current.line)) {
          setDraft('waiting');
          // Ember's draft from their conversation (a model read, so it may take a moment): the steps go on meanwhile.
          emberApi.friendDraft()
            .then((d) => {
              if (!alive) return;
              const drafted = (d.topics || []).slice(0, L.topics_max);
              setChips((c) => withChips(c, drafted));
              if (!touched.current.topics) setTopics(drafted.slice(0, CARD.TOPICS_TICKED));
              if (!touched.current.line && d.line) setLine(d.line);
              setDraft(d.line && !touched.current.line ? 'drafted' : 'none');
            })
            .catch((err: unknown) => {
              trackFailed('friends_draft', apiFailureReason(err));
              if (alive) setDraft('none');
            });
        }
        setState('ready');
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(`/friends/new${search.toString() ? `?${search.toString()}` : ''}`)}`, { replace: true }); return; }
        trackFailed('friends_setup_load', apiFailureReason(err));
        setState('failed');
      });
    return () => { alive = false; };
  }, []);

  // What they put in so far, kept across a reload (a deploy's own, or theirs): the step they were on and the card as it
  // stands — put back once the page has read where they stand, let go when the request is sent (2026-10-26 walk-through:
  // a deploy's reload on the third step sent them back to the first, their line and topic gone). Only a reload: leaving
  // the page lets it go, or Edit later opened on an edit they had walked away from (release check 2026-10-27).
  const keptKey = state === 'ready' ? `friends-setup:${fromId ?? ''}` : null;
  useKeptAcrossReload(keptKey, JSON.stringify({ step, gender, want, chips, topics, name, line, avatar }), (kept) => {
    try {
      const k = JSON.parse(kept) as { step: Step; gender: FriendPronoun | null; want?: FriendWant; chips: string[]; topics: string[]; name: string; line: string; avatar: string };
      // Only what they had filled counts as theirs: empty, Ember's draft still fills it (2026-10-27 walk-through: a reload
      // while the draft was coming left no topics ticked and an empty line for good).
      touched.current = { topics: k.topics.length > 0, line: Boolean(k.line) };
      // A step kept from the three-step setup (before 2026-10-31) is past the last one now: the card.
      setStep(Math.min(k.step, STEPS.length - 1) as Step); setGender(k.gender); setWant(k.want ?? 'anyone'); setChips(k.chips); setTopics(k.topics); setName(k.name); setLine(k.line); setAvatar(k.avatar);
    } catch { /* nothing to put back */ }
  }, { dropOnLeave: true });

  const go = (next: Step): void => {
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  const toggleTopic = (chip: string): void => {
    touched.current.topics = true;
    setTopics((list) => (list.some((t) => sameWord(t, chip))
      ? list.filter((t) => !sameWord(t, chip))
      : list.length < L.topics_max ? [...list, chip] : list));
  };
  const addOther = (): void => {
    const text = (other || '').trim().slice(0, L.topic_max_chars);
    if (!text) return;
    touched.current.topics = true;
    const known = chips.find((c) => sameWord(c, text)) ?? text;
    setChips((c) => withChips(c, [known]));
    setTopics((list) => (list.some((t) => sameWord(t, known)) || list.length >= L.topics_max ? list : [...list, known]));
    setOther(null);
  };

  const submit = async (): Promise<void> => {
    if (sending) return;
    setSending(true);
    const card: Partial<FriendCard> = { want, gender, topics, name: name.trim(), line: line.trim(), avatar, texting: true };
    try {
      await emberApi.fileFriendRequest(card);
      try { if (keptKey) window.sessionStorage.removeItem(keptKey); } catch { /* nothing kept */ }
      // Back to the /friends they came from, not a second one over it (release check 2026-10-27).
      goFriends();
    } catch (err: unknown) {
      const code = apiErrorCode(err);
      trackFailed('friends_request_submit', apiFailureReason(err));
      if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent('/friends/new')}`, { replace: true }); return; }
      if (code === 'not_eligible') { setState('unavailable'); return; }
      if (code === 'gender_required') { setGender(null); go(0); toast.info('One more thing first: which one fits you?'); return; }
      if (code === 'topics_required') { go(0); toast.info('Pick at least one thing to chat about.'); return; }
      // A name that would read as Ember, or words the review kept back: where they are, with why.
      if (code === 'name_taken') { toast.info('That name belongs to Ember here. Pick another?'); return; }
      if (code === 'line_required') { setLineOpen(true); toast.info('Add a line about you first.'); return; }
      const words = wordsRefused(err, { name: 'Your name', line: 'Your line', topics: 'A topic' });
      if (words) { if (words.field === 'topics') go(0); toast.info(words.message); return; }
      if (code === 'introduction_out') {
        // An introduction went out meanwhile: it is waiting for them.
        const page = await emberApi.friends().catch(() => null);
        const roomId = page?.friend?.intro?.room_id ?? page?.friend?.room_id;
        if (roomId) navigate(`/friends/intro/${encodeURIComponent(roomId)}`, { replace: true });
        else goFriends();
        return;
      }
      toast.error('Couldn’t save that. Try again?');
    } finally {
      setSending(false);
    }
  };

  if (state === 'loading') return <PageLoading />;
  if (state === 'unavailable') return <NotAvailable />;
  if (state === 'failed') return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;

  const me = gender;
  // What this step still needs, said above the button it holds back (2026-10-28 founder: a grey button with no word is a
  // dead end — almost no one has a name on file, so the card's name starts empty). The line is the card's, and a card
  // without one is refused (line_required, 2026-10-27).
  const missing = [
    !me ? 'Pick woman, man, or non-binary up top.' : topics.length === 0 ? 'Pick at least one thing to chat about.' : null,
    !name.trim() ? 'Your card needs a first name.' : line.trim() ? null : draft === 'waiting' ? 'One sec, I’m drafting your line.' : 'Your card needs a line about you.',
  ][step];
  const zone = zonePlace(myZone);
  const editingLine = lineOpen || (!line && draft !== 'waiting');

  return (
    <main className="nf-page">
      <FriendsBar
        left={step === 0 ? undefined : (
          <button type="button" className="nf-iconbtn" aria-label="Back" data-log="friends_setup_back" data-log-state={STEPS[step]} onClick={() => go((step - 1) as Step)}>{BACK_CHEVRON}</button>
        )}
      >
        <div className="nf-steps" role="img" aria-label={`Step ${step + 1} of ${STEPS.length}`}>
          {STEPS.map((s, i) => <span key={s} className={i <= step ? 'is-done' : ''} />)}
        </div>
      </FriendsBar>

      <div className="nf-step" key={step} data-log-seen="friends_setup" data-log-state={STEPS[step]}>
        {step === 0 && (
          <>
            <div className="nf-iam" role="group" aria-labelledby="nf-iam-label">
              <span className="nf-iam-label" id="nf-iam-label">First, I’m a…</span>
              <div className="nf-chips">
                {I_AM.map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={`nf-chip${gender === value ? ' is-on' : ''}`}
                    aria-pressed={gender === value}
                    data-log="friends_setup_gender"
                    data-log-state={value}
                    onClick={() => setGender(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="nf-iam" role="group" aria-labelledby="nf-meet-label">
              <span className="nf-iam-label" id="nf-meet-label">I’d like to meet…</span>
              <div className="nf-chips">
                {TO_MEET.map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={`nf-chip${want === value ? ' is-on' : ''}`}
                    aria-pressed={want === value}
                    data-log="friends_setup_want"
                    data-log-state={value}
                    onClick={() => setWant(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="nf-step-head">
              <span className="nf-kicker">pick up to {L.topics_max}</span>
              <h1 className="nf-title is-step">What could you chat about for hours?</h1>
            </div>
            <div className="nf-chips">
              {chips.map((chip, i) => {
                const on = topics.some((t) => sameWord(t, chip));
                return (
                  <button
                    type="button"
                    key={chip}
                    className={`nf-topic${on ? ' is-on' : ''}`}
                    style={on ? { background: tintOf(served.topics, chip, i), transform: `rotate(${TILTS[i % TILTS.length]})` } : undefined}
                    aria-pressed={on}
                    disabled={!on && topics.length >= L.topics_max}
                    data-log="friends_setup_topic"
                    data-log-state={on ? 'off' : 'on'}
                    onClick={() => toggleTopic(chip)}
                  >
                    {on && CHECK}
                    {chip}
                  </button>
                );
              })}
              {other === null ? (
                <button type="button" className="nf-topic is-other" disabled={topics.length >= L.topics_max} data-log="friends_setup_topic_other" onClick={() => setOther('')}>
                  {PLUS}
                  Something else
                </button>
              ) : (
                <form className="nf-other" onSubmit={(e) => { e.preventDefault(); addOther(); }}>
                  <input
                    autoFocus
                    value={other}
                    maxLength={L.topic_max_chars}
                    placeholder="Something else"
                    aria-label="Something else to chat about"
                    onChange={(e) => setOther(e.target.value)}
                  />
                  <button type="submit" disabled={!other.trim()} data-log="friends_setup_topic_add">Add</button>
                </form>
              )}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div className="nf-step-head">
              <span className="nf-kicker">last one!</span>
              <h1 className="nf-title is-step">Here’s how you’ll show up</h1>
              <p className="nf-step-sub">{draft === 'drafted' ? 'I drafted it from our chats. Change anything you like.' : 'Change anything you like.'}</p>
            </div>
            <section className="nf-card nf-me">
              <span className="nf-me-pic nf-bob"><PresenceAvatar avatarKey={avatar} size={112} /></span>
              <button type="button" className="nf-shuffle" data-log="friends_setup_shuffle" onClick={() => setAvatar(randomAvatar())}>{SHUFFLE}Shuffle</button>
              <input
                className={`nf-me-name${name.trim() ? '' : ' is-empty'}`}
                value={name}
                maxLength={L.name_max_chars}
                placeholder="Your first name"
                aria-label="Your first name"
                autoComplete="given-name"
                onChange={(e) => setName(e.target.value)}
              />
              {editingLine ? (
                <textarea
                  className="nf-me-line-edit"
                  value={line}
                  rows={3}
                  ref={fitHeight}
                  onInput={(e) => fitHeight(e.currentTarget)}
                  maxLength={L.line_max_chars}
                  placeholder="A line about you"
                  aria-label="Your line"
                  autoFocus={lineOpen}
                  // Typing into an empty line keeps the box open (2026-10-27 walk-through: it turned into the line read-only after
                  // the first letter, and the card went out as "I").
                  onChange={(e) => { touched.current.line = true; setLineOpen(true); setLine(e.target.value); }}
                />
              ) : (
                <p className="nf-me-line">{line || <span className="nf-me-drafting">Drafting your line…</span>}</p>
              )}
              {zone && <div className="nf-me-meta">{zone}</div>}
              {topics.length > 0 && (
                <div className="nf-chips is-center">
                  {topics.map((t, i) => (
                    <span className="nf-tag" key={t} style={{ background: tintOf(served.topics, t, i), transform: `rotate(${TILTS[i % TILTS.length]})` }}>{t}</span>
                  ))}
                </div>
              )}
              {!editingLine && line && (
                <button type="button" className="nf-link-btn" data-log="friends_setup_line_edit" onClick={() => setLineOpen(true)}>{PENCIL}Edit my line</button>
              )}
            </section>
            <p className="nf-info">{INFO}<span>{served.words.card_shown}</span></p>
          </>
        )}
      </div>

      <div className="nf-foot">
        {step === 1 && <p className="nf-foot-note">{served.words.card_seen}</p>}
        {missing && <p className="nf-foot-note is-missing" role="status">{missing}</p>}
        <button
          type="button"
          className="nf-cta is-block"
          disabled={Boolean(missing) || sending}
          data-log={step === 1 ? 'friends_request_submit' : 'friends_setup_next'}
          data-log-state={step === 1 ? mode : STEPS[step]}
          onClick={() => { if (step === 1) void submit(); else go((step + 1) as Step); }}
        >
          {step < 1 ? 'Next' : mode === 'edit' ? 'Save changes' : 'Find me someone'}
        </button>
      </div>
    </main>
  );
}
